import {
  Dispatch,
  FunctionComponent,
  ReactNode,
  SetStateAction,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Route, Routes, useLocation, useParams } from 'react-router-dom';
import LoadingComponent from '../core/componentLoading/loading';
import { FormValues, PrefillContext } from '../core/contexte/prefillContext';
import SaveAndRetrieveProvider from '../core/contexte/saveAndRetrieveContext';

import api from '../api';
import { constants } from '../constants';
import {
  AMEXKYCERRORS,
  AMEXKYCVALIDATION,
  VISA_SteuerlandNichtD1_ERRORS,
  VISA_VALIDATION,
} from '../constants/amex_complexValidations';
import LazyComponentLoader from '../core/componentLoading/lazyComponentLoader';
import GenericErrorPage from '../core/genericErrorPage';
import { GlobalComponentLoader } from '../core/services/componentLoader';
import { FormPage } from './formPage';
import ComponentBuilder from '../core/formBuilder/componentBuilder';
import FormularElement from '../core/formBuilder/formularElement';
import { ConfigContextProvider } from '../core/formBuilder/configContext';
import IntlContext from '../core/formBuilder/intlContext';
import CustomErrorPage from '../core/CustomErrorPage';
import { useErrorPage } from '../core/globalErrorContext';
import { Metadata, MetadataProvider } from '../core/contexte/metadataContext';
import { getDocumentTitle } from '../core/services/metadataUtils';
import { PageTypeProvider } from '../core/contexte/PageTypeContext';
import {
  FbFormPagesTranslationConfig,
  FbPageLayoutTranslation,
  FbPropTranslation,
  FbStaticPageTranslationConfig,
  FbTranslationDto,
} from '../components/pageLayout/localization/types';
import { getEnvVariableByKey } from '../utils/tenantUtils';
import { DropDownOption } from '../components/@material/conditionalDropdownField/interfaces/IDropDownField';
import { PageLayout } from '../core/formBuilder/usePages';
import { FormStateContextProvider } from './formState';
import { ConfigMetaData } from '../core/formBuilder/metaDataContext';
import { API } from '../api/apiConstants';
import { FORM_SUBMITTED_KEY } from '../utils/sessionStorageKeys';
import { removeSessionFlag } from '../utils/sessionStorageUtils';
import { FormElementRegistryProvider } from '../core/contexte/FormElementRegistryContext';
import StaticPageFormContainer from '../core/staticPageFormContainer';
import { isStaticPageWithSubmitButton } from './pageUtils';
import { IdScanLocalizationProvider } from '../components/blinkIdLocalization/IdScanLocalizationContext';
import { TimerManagerProvider } from '../core/contexte/timerManagerContext';
import { PublicRouteProvider } from '../core/contexte/PublicRouteContext';
import { BackgroundColorProvider } from '../core/contexte/backgroundColorContext';

interface ProjectPageProps {
  setTheme: Dispatch<SetStateAction<{}>>;
}

export const ProjectPage: FunctionComponent<ProjectPageProps> = ({
  setTheme,
}) => {
  const { project } = useParams();
  const { search } = useLocation();
  const { handleError } = useErrorPage();
  const { setDefaultValues } = useContext(PrefillContext);
  const [config, setConfig] = useState<{
    form: object | FormularElement;
    intl: any;
    meta: ConfigMetaData;
    dataSchema: {
      errorSchema: Record<string, Record<string, string>>;
      validationSchema: {};
    };
    technicalComponents: any;
    theme_config: any;
    staticPagesConfig: FormularElement[];
    defaultValues: any[];
    thirdPartySystems: any;
    translationConfig: FbTranslationDto | undefined;
  }>({
    form: {},
    intl: null,
    meta: {},
    dataSchema: {
      errorSchema: {},
      validationSchema: {},
    },
    technicalComponents: undefined,
    theme_config: undefined,
    staticPagesConfig: [],
    defaultValues: [],
    thirdPartySystems: undefined,
    translationConfig: undefined,
  });
  const [selectedLanguage, setSelectedLanguage] = useState<string>('DE');
  const urlSearchParams = new URLSearchParams(search);
  const metadata = {
    documentTitle: getDocumentTitle(project, config.intl?.['documentTitle']),
    lang: config.intl?.['lang'] || 'de',
    favicon: config?.meta?.favicon,
  };

  const renderCustomStaticPages = () => {
    // TODO remove
    return (
      constants.PATH_FOR_CUSTOM_STATIC_PAGES && (
        <LazyComponentLoader
          key={'CustomStaticPages'}
          excludeGrid={true}
          refComponent={`asb_modules/${constants.PATH_FOR_CUSTOM_STATIC_PAGES}/CustomStaticRoutes`}
        />
      )
    );
  };

  const renderFillIndexHTML = () => {
    // TODO should be replaced by global technical components
    return (
      constants.PATH_FOR_CUSTOM_STATIC_PAGES && (
        <LazyComponentLoader
          key={'FillIndexHTML'}
          excludeGrid={true}
          refComponent={`asb_modules/${constants.PATH_FOR_CUSTOM_STATIC_PAGES}/indexHTML/FillIndexHTML`}
        />
      )
    );
  };

  const renderLazy = (page: FormularElement) => {
    return (
      <ComponentBuilder
        element={page}
        formThirdPartySystemsConfig={config?.thirdPartySystems}
      />
    );
  };

  const fillConfig = (data: any) => {
    if (data.theme_config) setTheme(data.theme_config);
    // must be the name of the current project
    if (project === 'amex' || project === 'amexVers' || project === 'amexv2') {
      addAmexKYCValidation(data);
      addAmexKYCErrors(data);
    } else if (project === 'visa' || project === 'visaguthaben') {
      addVISA_VALIDATION(data);
      addVISA_SteuerlandNichtD1_ERRORS(data);
    }
    setDefaultValues(
      data?.defaultValues?.reduce(
        (
          acc: FormValues,
          defaultValue: {
            fieldName: string;
            value: string;
          }
        ) => ({ ...acc, [defaultValue.fieldName]: defaultValue.value }),
        {}
      )
    );
    setConfig(data);
    let selectedLanguage = urlSearchParams.get('lang')?.toUpperCase();
    const translationConfig = data.translationConfig as FbTranslationDto | null;
    if (
      selectedLanguage === null ||
      selectedLanguage === undefined ||
      !translationConfig?.availableTranslations ||
      !Object.keys(translationConfig.availableTranslations).includes(
        selectedLanguage
      )
    ) {
      selectedLanguage = translationConfig?.defaultTranslationName ?? 'DE';
    }
    if (getEnvVariableByKey('REACT_APP_ENABLE_LOCALIZATION') === 'true') {
      setSelectedLanguage(selectedLanguage ?? 'DE');
    }
  };

  // Clear the submitted flag to prevent subsequent manual redirects to success page on fresh URL navigation
  useEffect(() => {
    const navEntry = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (navEntry?.type === 'navigate') {
      removeSessionFlag(FORM_SUBMITTED_KEY);
    }
  }, []);

  useEffect(() => {
    if (JSON.stringify(config.form) === '{}') {
      // TODO: fix this
      if (urlSearchParams.has('version'))
        api.Public.getConfigByVersion(project, urlSearchParams.get('version'))
          .then((data) => {
            if (data.error) {
              handleError({
                text: data?.message,
                status: data?.status,
              });
              return;
            }
            fillConfig(data);
          })
          .catch((error) => console.warn(error));
      else
        api.Public.getActiveConfigByProjectName(project)
          .then((data) => {
            if (data.error) {
              handleError({
                text: data?.message,
                status: data?.status,
              });
              return;
            }
            fillConfig(data);
          })
          .catch((error) => console.warn(error));
    }
  }, [config.form, project]);

  const translations = config.translationConfig?.availableTranslations;
  const translationConfig =
    translations !== undefined ? translations[selectedLanguage] : undefined;

  const translatedIntlSettings = useMemo(() => {
    return translationConfig?.intlSettings !== undefined &&
      getEnvVariableByKey('REACT_APP_ENABLE_LOCALIZATION') === 'true'
      ? mergeIntlSettings(metadata, translationConfig.intlSettings)
      : metadata;
  }, [selectedLanguage, metadata, config.intl, config.translationConfig]);

  useEffect(() => {
    if (metadata.favicon?.identifier) {
      let link: HTMLLinkElement | null =
        document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.type = metadata.favicon.type || 'image/x-icon';
      link.href = API.V3.STATIC_RESOURCES.STATIC_RESOURCE(
        metadata.favicon.identifier
      );
    }
  }, [metadata.favicon]);

  useEffect(() => {
    if (translatedIntlSettings.documentTitle) {
      document.title = translatedIntlSettings.documentTitle;
    }

    document.documentElement.lang = config?.translationConfig
      ?.translationEnabled
      ? selectedLanguage.toString().toLowerCase()
      : metadata.lang || 'de';
  }, [
    translatedIntlSettings,
    metadata.documentTitle,
    metadata.lang,
    selectedLanguage,
  ]);

  const bgColor = config.theme_config?.['palette']?.['background']?.['default'];

  const translatedForm = useMemo(() => {
    return translationConfig?.formPages !== undefined &&
      getEnvVariableByKey('REACT_APP_ENABLE_LOCALIZATION') === 'true'
      ? overwriteWithTranslation(
          config.form as FormularElement,
          translationConfig.formPages
        )
      : config.form;
  }, [selectedLanguage, config.form, config.translationConfig]);

  const translatedErrorSchema = useMemo(() => {
    return translationConfig?.formPages != undefined &&
      getEnvVariableByKey('REACT_APP_ENABLE_LOCALIZATION') === 'true'
      ? overwriteErrorWithTranslation(
          config.dataSchema.errorSchema,
          translationConfig.formPages
        )
      : config.dataSchema.errorSchema;
  }, [
    selectedLanguage,
    config.dataSchema.errorSchema,
    config.translationConfig?.availableTranslations,
  ]);

  const translatedStaticPages = useMemo(() => {
    return translationConfig?.staticPages !== undefined &&
      getEnvVariableByKey('REACT_APP_ENABLE_LOCALIZATION') === 'true'
      ? overwriteWithStaticArrayTranslation(
          config.staticPagesConfig,
          translationConfig.staticPages
        )
      : config.staticPagesConfig;
  }, [
    selectedLanguage,
    config.staticPagesConfig,
    config.translationConfig?.availableTranslations,
  ]);

  // Build a fieldName-element lookup from the entire form tree.
  const formFieldNameMap = useMemo(
    () => buildFieldNameMap(translatedForm as FormularElement),
    [translatedForm]
  );

  // Resolve component to render based on fieldName lookup
  const renderRegisteredElement = useCallback(
    (fieldName: string): ReactNode => {
      const element = formFieldNameMap.get(fieldName);
      return element ? (
        <ComponentBuilder
          element={element}
          formThirdPartySystemsConfig={config?.thirdPartySystems}
        />
      ) : null;
    },
    [formFieldNameMap, config?.thirdPartySystems]
  );

  return (
    <MetadataProvider metadata={metadata} project={project}>
      <SaveAndRetrieveProvider>
        <PublicRouteProvider staticPagesConfig={config.staticPagesConfig}>
          <Suspense fallback={<LoadingComponent />}>
            <GlobalComponentLoader
              globalComponentConfig={config.technicalComponents}
            />
            {renderFillIndexHTML()}
          </Suspense>
        </PublicRouteProvider>
        <Suspense fallback={<LoadingComponent />}>
          <BackgroundColorProvider globalColor={bgColor}>
            {!!Object.keys(config.form).length && (
              <IntlContext.Provider value={config.intl}>
                <ConfigContextProvider
                  form={translatedForm}
                  thirdPartySystems={config.thirdPartySystems}
                  translationConfig={config.translationConfig}
                  setSelectedLanguage={setSelectedLanguage}
                  selectedLanguage={selectedLanguage}
                >
                  <IdScanLocalizationProvider>
                    <TimerManagerProvider>
                      <PageTypeProvider
                        staticRoutes={[
                          ...(translatedStaticPages
                            .map((page: FormularElement) => page.route)
                            .filter((route): route is string =>
                              Boolean(route)
                            ) || []),
                        ]}
                      >
                        <FormElementRegistryProvider
                          renderElement={renderRegisteredElement}
                        >
                          <FormStateContextProvider>
                            <Routes>
                              <Route
                                path={'error'}
                                element={
                                  config?.staticPagesConfig?.some(
                                    (page: FormularElement) =>
                                      page.route === '/error'
                                  ) ? (
                                    <CustomErrorPage
                                      staticPagesConfig={translatedStaticPages}
                                    />
                                  ) : (
                                    <GenericErrorPage />
                                  )
                                }
                              />
                              {translatedStaticPages
                                .filter((page: FormularElement) => page.route)
                                .map((page: FormularElement) => (
                                  <Route
                                    key={page.route}
                                    path={page.route + '/*'}
                                    element={
                                      <StaticPageFormContainer
                                        requiresSession={isStaticPageWithSubmitButton(
                                          page
                                        )}
                                      >
                                        {renderLazy(page)}
                                      </StaticPageFormContainer>
                                    }
                                  />
                                ))}

                              <Route
                                path={'/static/*'}
                                element={renderCustomStaticPages() ?? <></>}
                              />
                              <Route
                                path="*"
                                element={
                                  <FormPage
                                    config={{
                                      ...config,
                                      form: translatedForm,
                                      dataSchema: {
                                        validationSchema:
                                          config.dataSchema.validationSchema,
                                        errorSchema: translatedErrorSchema,
                                      },
                                    }}
                                  />
                                }
                              />
                            </Routes>
                          </FormStateContextProvider>
                        </FormElementRegistryProvider>
                      </PageTypeProvider>
                    </TimerManagerProvider>
                  </IdScanLocalizationProvider>
                </ConfigContextProvider>
              </IntlContext.Provider>
            )}
          </BackgroundColorProvider>
        </Suspense>
      </SaveAndRetrieveProvider>
    </MetadataProvider>
  );
};

function addAmexKYCValidation(data: any) {
  data.dataSchema.validationSchema = {
    ...data.dataSchema.validationSchema,
    allOf: [
      ...(data?.dataSchema?.validationSchema?.allOf ?? []),
      ...AMEXKYCVALIDATION,
    ],
  };
}

function addAmexKYCErrors(data: any) {
  data.dataSchema.errorSchema = {
    ...data.dataSchema.errorSchema,
    ...AMEXKYCERRORS,
  };
}

function addVISA_VALIDATION(data: any) {
  data.dataSchema.validationSchema = {
    ...data.dataSchema.validationSchema,
    allOf: [
      ...(data?.dataSchema?.validationSchema?.allOf ?? []),
      ...VISA_VALIDATION,
    ],
  };
}

function addVISA_SteuerlandNichtD1_ERRORS(data: any) {
  data.dataSchema.errorSchema = {
    ...data.dataSchema.errorSchema,
    ...VISA_SteuerlandNichtD1_ERRORS,
  };
}

export function overwriteWithTranslation(
  form: FormularElement,
  translation: FbFormPagesTranslationConfig
) {
  let copy = { ...form };
  if (copy.fieldName != undefined) {
    const translationProps =
      translation.elementTranslations[copy.fieldName]?.props;
    if (translationProps !== undefined) {
      const stringTranslations = getStringEntries(translationProps);
      copy = { ...copy, ...stringTranslations };
      if (copy.options !== undefined) {
        copy.options = translateOptions(copy.options, translationProps);
      }
    }
  } else if (
    copy.refComponent == 'components/@material/page' &&
    copy.id != undefined
  ) {
    const translationProps = translation.pageTranslations[copy.id];
    copy = {
      ...copy,
      label: translationProps?.label,
      description: translationProps?.description,
    };
    if (copy.pageLayout !== undefined) {
      copy.pageLayout = modifyPageLayout(
        copy.pageLayout,
        translationProps?.pageLayout
      );
    }
  }
  copy.elements = copy.elements?.map((child) => {
    return overwriteWithTranslation(child, translation);
  });
  return copy;
}

function getStringEntries(entries: FbPropTranslation) {
  return Object.fromEntries(
    Object.entries(entries).filter(([_, value]) => typeof value === 'string')
  ) as Record<string, string>;
}

function translateOptions(
  options: DropDownOption[],
  translationProps: FbPropTranslation
) {
  const optionTranslations = translationProps.options;
  return optionTranslations !== undefined
    ? options.map((option) => {
        const overwrite = optionTranslations[option.key];
        let option_copy = { ...option };
        if (overwrite !== undefined) {
          option_copy = { ...option, ...getStringEntries(overwrite) };
          if (option_copy.options !== undefined) {
            option_copy.options = translateOptions(
              option_copy.options,
              overwrite
            );
          }
        }

        return option_copy;
      })
    : options;
}

function overwriteErrorWithTranslation(
  errorSchema: Record<string, Record<string, string>>,
  translation: FbFormPagesTranslationConfig
) {
  const copy: Record<string, Record<string, string>> = {};
  for (const [key, value] of Object.entries(errorSchema)) {
    const translationProps = translation.elementTranslations[key]?.validations;
    copy[key] = { ...value, ...translationProps };
  }
  return copy;
}

export function overwriteWithStaticArrayTranslation(
  pages: FormularElement[],
  translation: Record<string, FbStaticPageTranslationConfig | undefined>
) {
  return pages.map((page) => {
    const pageTranslation =
      page.id!! in translation ? translation[page.id!!] : undefined;
    return pageTranslation != undefined
      ? overwriteStaticPageLayout(
          overwriteWithStaticTranslation(page, pageTranslation),
          pageTranslation
        )
      : page;
  });
}

function overwriteStaticPageLayout(
  element: FormularElement,
  translation: FbStaticPageTranslationConfig
) {
  const elementCopy = {
    ...element,
    labelErrorRestartButton:
      translation.staticPageTranslations?.labelErrorRestartButton,
  };
  const errorsMap = translation.staticPageTranslations?.errorsMap ?? {};
  elementCopy.errorsMap = elementCopy.errorsMap?.map(({ key, value }) => {
    return { key, value: errorsMap[key] ?? value };
  });
  if (elementCopy.pageLayout !== undefined) {
    elementCopy.pageLayout = modifyPageLayout(
      elementCopy.pageLayout,
      translation.staticPageTranslations?.pageLayout
    );
  }

  return elementCopy;
}

function modifyPageLayout(
  layout: PageLayout,
  translation?: FbPageLayoutTranslation
) {
  if (translation !== undefined) {
    const copy = { ...layout };
    (['header', 'footer'] as const).forEach((prop) => {
      const orgAttribute =
        copy[prop] !== undefined ? { ...copy[prop] } : undefined;
      const attributeTranslation = translation[prop] ?? {};
      if (translation !== undefined && orgAttribute !== undefined) {
        orgAttribute.segments = orgAttribute.segments?.map((segment, index) => {
          return {
            ...segment,
            text: attributeTranslation['' + index] ?? segment.text,
          };
        });
      }
      copy[prop] = orgAttribute;
    });
    return copy;
  }
  return layout;
}

function overwriteWithStaticTranslation(
  element: FormularElement,
  translation: FbStaticPageTranslationConfig
) {
  let copy = { ...element };
  const elementTranslations = translation.elementTranslations ?? {};
  if (copy.fieldName != undefined) {
    const translationProps = elementTranslations[copy.fieldName]?.props;
    copy = { ...copy, ...translationProps };
  }
  copy.elements = copy.elements?.map((child) => {
    return overwriteWithStaticTranslation(child, translation);
  });
  return copy;
}

function mergeIntlSettings(
  current: Metadata,
  translation: Partial<Record<string, string | undefined>>
): Metadata {
  const result: Metadata = { ...current };
  Object.keys(translation).forEach((key) => {
    const value = translation[key];
    if (value !== null && value !== undefined && value !== '') {
      result[key as keyof Metadata] = value;
    }
  });
  return result;
}

function buildFieldNameMap(
  element: FormularElement,
  map: Map<string, FormularElement> = new Map()
): Map<string, FormularElement> {
  if (element.fieldName) map.set(element.fieldName, element);
  element.elements?.forEach((child) => buildFieldNameMap(child, map));
  return map;
}
